import _ from "lodash";
import React, { PureComponent, ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utilty/main-functions";
import { BaseTable, ViewMode } from "../../base-table";

declare global {
  interface Window {
    kuika?: any;
    nextCellToFocus: {
      tableId: string;
      columnIndex: number;
      rowIndex: string;
    } | null;
    handlingCellClick?: boolean;
  }
}

interface TableBodyCellProps {
  children?: ReactNode;
  calculatedCellBgColor?: (rowIndex?: number, rowData?: any) => string;
  calculatedCellFontColor?: (rowIndex?: number, rowData?: any) => string;
  style?: any;
  rowData?: any;
  rowIndex?: number;
  columnIndex: number;
  title: string;
  mode?: ViewMode;
  onCellClick: (e: any, columnIndex?: number, rowData?: any, rowIndex?: number) => void;
  onCellBlur: (e: any) => void;
  isEditable?: boolean;
  isEditMode?: boolean;
  dataValue?: any;
  editableAlwaysActive?: boolean;
  setResizingColumnIndex?: (index: number, startX: number) => void;
  resizeListener?: (e: MouseEvent) => void;
  index?: number;
  resizableColumns?: boolean;
  tableId?: string;
  parentTable?: BaseTable<any, any>;
  fixed?: "left" | "right";
  left?: number;
  right?: number;
  elementsDirection?: "horizontal" | "vertical";
  elementsGap?: number;
}
interface TableBodyCellState {}

export class TableBodyCell extends PureComponent<TableBodyCellProps, TableBodyCellState> {
  private cellRef: React.RefObject<HTMLTableCellElement>;

  constructor(props: TableBodyCellProps) {
    super(props);
    this.cellRef = React.createRef();
  }

  getStyle = (): React.CSSProperties => {
    let style: React.CSSProperties = {};
    if (this.props.style) {
      style = _.clone(this.props.style);
    }
    if (this.props.calculatedCellBgColor) {
      style.background = this.props.calculatedCellBgColor(this.props.rowIndex, this.props.rowData);
    }
    if (this.props.calculatedCellFontColor) {
      style.color = this.props.calculatedCellFontColor(this.props.rowIndex, this.props.rowData);
    }
    if (
      !style.border &&
      !style.borderColor &&
      !style.borderTop &&
      !style.borderBottom &&
      !style.borderRight &&
      !style.borderLeft &&
      !style.borderWidth
    ) {
      style.borderWidth = 0;
    }
    if (style?.display) {
      delete style.display;
    }
    if (!style.borderStyle) {
      style.borderStyle = "solid";
    }
    if (!style?.padding) {
      style.padding = "0px";
    }
    if (style.padding && (style?.paddingLeft || style?.paddingRight || style?.paddingTop || style?.paddingBottom)) {
      delete style.padding;
    }
    if (window.kuika.dashboardState === 17) {
      style.boxSizing = "border-box";

      if (style.width) {
        style.minWidth = style.width;
        style.maxWidth = style.width;
      }
      if (style.height) {
        style.minHeight = style.height;
        style.maxHeight = style.height;
      }

      if (style.alignItems) style.alignContent = style.alignItems;
      else style.alignContent = "flex-start";

      if (!style.paddingLeft) style.paddingLeft = "0px";
      if (!style.paddingRight) style.paddingRight = "0px";
      if (!style.paddingTop) style.paddingTop = "0px";
      if (!style.paddingBottom) style.paddingBottom = "0px";
    }

    if (this.props.fixed === "left" && this.props.left && !window.kuika.isDesignTime) {
      style.left = this.props.left;
    }
    if (this.props.fixed === "right" && this.props.right && !window.kuika.isDesignTime) {
      style.right = this.props.right;
    }
    return style;
  };

  isRadioGroupElement = (element: HTMLElement): boolean => {
    if (!element) return false;

    const target = element as HTMLElement;
    const targetClass = target.className;
    const targetTag = target.tagName?.toLowerCase();

    if (targetTag === "input" && (target as HTMLInputElement).type === "radio") {
      return true;
    }

    if (targetTag === "input" && (target as HTMLInputElement).type === "checkbox") {
      const parentSpan = target.closest(".kuika-radio-group__radio");
      if (parentSpan) return true;
    }

    if (targetTag === "label") {
      const parentSpan = target.closest(".kuika-radio-group__radio");
      if (parentSpan) return true;
    }

    if (
      typeof targetClass === "string" &&
      (targetClass.includes("kuika-radio-group") ||
        targetClass.includes("ant-radio-button") ||
        targetClass.includes("ant-radio-button-input"))
    ) {
      return true;
    }

    const radioGroupParent = target.closest(".kuika-radio-group");
    return !!radioGroupParent;
  };

  handleCellClick = (e: any) => {
    if (this.isRadioGroupElement(e.target)) {
      return;
    }

    if (this.props.onCellClick) {
      this.props.onCellClick(e);
    }
  };

  handleCellBubble = (e: any) => {
    if (this.isRadioGroupElement(e.target)) {
      e.stopPropagation();
    }
  };

  handleCellBlur = (e: any) => {
    if (this.props.onCellBlur) {
      this.props.onCellBlur(e);
    }
  };

  applyFlexStylesToDOM = () => {
    if (window.kuika.dashboardState !== 17) {
      return;
    }

    const cellElement = this.cellRef.current;
    if (!cellElement) {
      return;
    }

    // Try to get the child container element
    const childrenArray = React.Children.toArray((this.props.children as any)?.[1]?.props?.children?.props?.children);
    const elementsDirection = (this.props.children as any)?.[1]?.props?.children?.props?.elementsDirection;
    const elementsGap = (this.props.children as any)?.[1]?.props?.children?.props?.elementsGap;

    if (childrenArray.length > 0 && (elementsDirection || elementsGap)) {
      const containerElement = cellElement.querySelector("div") as HTMLElement;

      if (containerElement) {
        containerElement.style.display = "flex";
        containerElement.style.width = "100%";
        containerElement.style.height = "100%";
        containerElement.style.flexDirection = elementsDirection === "vertical" ? "column" : "row";
        if (elementsGap && elementsGap > 0) {
          containerElement.style.gap = `${elementsGap}pt`;
        }

        const style = this.props.style || {};
        const { textAlign, alignItems } = style;

        if (elementsDirection === "vertical") {
          if (this.props.style.alignItems) {
            containerElement.style.justifyContent = this.props.style.alignItems;
          } else {
            containerElement.style.justifyContent = "flex-start";
          }

          if (this.props.style.textAlign) {
            const horizontalAlign = KMainFunctions.getTextAlignAsJustifyContent(this.props.style.textAlign);
            containerElement.style.alignItems = horizontalAlign || "flex-start";
            containerElement.style.textAlign = this.props.style.textAlign;
          } else {
            containerElement.style.alignItems = "flex-start";
            containerElement.style.textAlign = "";
          }
        } else {
          if (this.props.style.alignItems) {
            containerElement.style.alignContent = this.props.style.alignItems;
          } else {
            containerElement.style.alignContent = "flex-start";
          }

          if (this.props.style.textAlign) {
            const justifyContent = KMainFunctions.getTextAlignAsJustifyContent(this.props.style.textAlign);
            containerElement.style.justifyContent = justifyContent;
            containerElement.style.textAlign = this.props.style.textAlign;
          }
        }
      }
    }
  };

  filterNodeByKuikaComponentName(node) {
    if (!node || !node.props) {
      return true;
    }
    const { props } = node;

    if (props && props.kuikacomponentname != "Label") {
      return node;
    }

    if (props && props.children) {
      const filteredChildren = React.Children.map(props.children, (child) =>
        this.filterNodeByKuikaComponentName(child)
      );

      const hasFilteredChildren = filteredChildren.some((child) => child !== null);
      if (hasFilteredChildren) {
        return {
          ...node,
          props: {
            ...props,
            children: filteredChildren
          }
        };
      }
    }

    return null;
  }

  componentDidMount(): void {
    if (
      this.props.editableAlwaysActive &&
      this.props.columnIndex?.toString() === window.nextCellToFocus?.columnIndex?.toString() &&
      this.props.rowIndex?.toString() === window.nextCellToFocus?.rowIndex?.toString()
    ) {
      setTimeout(() => {
        const cellElement = this.getThisCellElement();
        if (cellElement) {
          let inputElement = cellElement.querySelector("input") as HTMLInputElement;
          if (inputElement && document.activeElement !== inputElement) {
            if (this.props.columnIndex !== undefined && this.props.rowIndex !== undefined) {
              inputElement.focus();

              if (this.props.onCellClick && !window.handlingCellClick) {
                window.handlingCellClick = true;

                const syntheticEvent = {
                  currentTarget: cellElement,
                  target: inputElement,
                  preventDefault: () => {},
                  stopPropagation: () => {}
                };
                this.props.onCellClick(syntheticEvent, this.props.columnIndex, this.props.rowData, this.props.rowIndex);
                setTimeout(() => {
                  window.handlingCellClick = false;
                  window.nextCellToFocus = null;
                }, 500);
              }
            }
          }
        }
      }, 500);
    }

    if (window.nextCellToFocus === undefined) {
      window.nextCellToFocus = null;
    }

    // Apply flex styles to DOM after mount
    this.applyFlexStylesToDOM();
  }

  componentDidUpdate(): void {
    // Apply flex styles to DOM after update
    this.applyFlexStylesToDOM();
  }

  getThisCellElement = () => {
    let cellElement: HTMLElement | null = null;
    const tableId = this.props.tableId;
    if (!tableId) {
      return null;
    }
    const tableElement = window.document.querySelector(`[id="${tableId}"]`) as HTMLElement;
    if (!tableElement) {
      return null;
    }
    const rowWithId = tableElement.querySelector(`tr[data-row-key="${this.props.rowData?.id}"]`) as HTMLElement;

    if (rowWithId) {
      cellElement = rowWithId.querySelector(`td[columnindex="${this.props.columnIndex}"]`) as HTMLElement;
    }

    if (!cellElement) {
      const tableRows = tableElement.querySelectorAll("tr");

      for (const row of tableRows) {
        const rowDataAttr = row.getAttribute("data-row-key");
        const rowIndexAttr = row.getAttribute("rowindex");

        if (
          (rowDataAttr && rowDataAttr === this.props.rowData?.id?.toString()) ||
          (rowIndexAttr && rowIndexAttr === this.props.rowIndex?.toString() && this.props.rowIndex !== undefined)
        ) {
          cellElement = row.querySelector(`td[columnindex="${this.props.columnIndex}"]`) as HTMLElement;
          break;
        }
      }
    }

    return cellElement;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    props.tableTitle = this.props.title;
    props.dataValue = this.props.dataValue;
    props.title = "";
    const screenWidth = window.innerWidth;
    if (this.props.resizableColumns && !window.kuika.isDesignTime && screenWidth > 1024) {
      delete props.children;
    }
    return props;
  };

  render(): ReactNode {
    const screenWidth = window.innerWidth;

    if (this.props.resizableColumns && !window.kuika.isDesignTime && screenWidth > 1024) {
      return (
        <td
          ref={this.cellRef}
          onClickCapture={this.handleCellClick}
          onClick={this.handleCellBubble}
          {...this.getProps()}
          style={this.getStyle()}
          onBlur={this.handleCellBlur}
          tabIndex={this.props.editableAlwaysActive ? 0 : undefined}
        >
          <div
            className="resizeHandle"
            onMouseDown={(e) => {
              if (this.props.setResizingColumnIndex && this.props.index !== undefined) {
                this.props.setResizingColumnIndex(this.props.index, e.clientX);
                window.addEventListener("mousemove", this.props.resizeListener, { passive: true });
              }
            }}
          />
          {this.props.children}
        </td>
      );
    }
    return (
      <td
        ref={this.cellRef}
        onClickCapture={this.handleCellClick}
        onClick={this.handleCellBubble}
        {...this.getProps()}
        style={this.getStyle()}
        onBlur={this.handleCellBlur}
        tabIndex={this.props.editableAlwaysActive ? 0 : undefined}
      />
    );
  }
}
